// Layout pieces: Card, Breadcrumb, PageHeader, EmptyState, Tabs, Tooltip.
import React, { cloneElement, forwardRef, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const cx = (...names) => names.filter(Boolean).join(' ');

export const Card = forwardRef(function Card({ as: Comp = 'div', padding = 'md', className, children, ...rest }, ref) {
  return <Comp ref={ref} className={cx('ui-card', `ui-card--pad-${padding}`, className)} {...rest}>{children}</Comp>;
});

/** items: [{label, to?}] — the last one is the current page. */
export function Breadcrumb({ items }) {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;
  return (
    <nav aria-label={t('ui.breadcrumb')} className="ui-breadcrumb">
      <ol>
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={`${index}-${item.label}`}>
              {item.to && !last ? <Link to={item.to}>{item.label}</Link> : <span aria-current={last ? 'page' : undefined}>{item.label}</span>}
              {!last && <span className="ui-breadcrumb__sep" aria-hidden="true">›</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * The header of every page: breadcrumb, h1, a line of counts, actions on
 * the right (they wrap under the title on narrow screens).
 */
export function PageHeader({ breadcrumb, title, subtitle, actions, className, children }) {
  return (
    <header className={cx('ui-page-header', className)}>
      <div className="ui-page-header__text">
        <Breadcrumb items={breadcrumb} />
        <h1 className="ui-page-header__title">{title}</h1>
        {subtitle && <p className="ui-page-header__subtitle">{subtitle}</p>}
        {children}
      </div>
      {actions && <div className="ui-page-header__actions">{actions}</div>}
    </header>
  );
}

export function EmptyState({ icon: Icon, title, titleAs: Title = 'h2', children, action, className }) {
  return (
    <div className={cx('ui-empty', className)}>
      {Icon && <span className="ui-empty__icon"><Icon size={22} /></span>}
      {title && <Title className="ui-empty__title">{title}</Title>}
      {children && <div className="ui-empty__text">{children}</div>}
      {action && <div className="ui-empty__action">{action}</div>}
    </div>
  );
}

/**
 * Tabs with roving focus (arrows, Home, End). `children` is the selected
 * tab's panel. tabs: [{id, label}].
 */
export function Tabs({ tabs, value, onChange, label, className, children }) {
  const base = useId();
  const refs = useRef({});
  const move = (event, index) => {
    let next = null;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    onChange(tabs[next].id);
    const node = refs.current[tabs[next].id];
    if (node) node.focus();
  };
  return (
    <div className={cx('ui-tabs', className)}>
      <div role="tablist" aria-label={label} className="ui-tabs__list">
        {tabs.map((tab, index) => {
          const selected = tab.id === value;
          return (
            <button
              key={tab.id}
              ref={(node) => { refs.current[tab.id] = node; }}
              type="button"
              role="tab"
              id={`${base}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${base}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={cx('ui-tabs__tab', selected && 'is-selected')}
              onClick={() => onChange(tab.id)}
              onKeyDown={(e) => move(e, index)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {children != null && (
        <div role="tabpanel" id={`${base}-panel-${value}`} aria-labelledby={`${base}-tab-${value}`} tabIndex={0} className="ui-tabs__panel">
          {children}
        </div>
      )}
    </div>
  );
}

/** A short text shown on hover and keyboard focus of its single child. */
export function Tooltip({ content, children, placement = 'top' }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const child = React.Children.only(children);
  const show = () => setOpen(true);
  const hide = () => setOpen(false);
  return (
    <span className="ui-tooltip-anchor" onMouseEnter={show} onMouseLeave={hide}
          onKeyDown={(e) => { if (e.key === 'Escape') hide(); }}>
      {cloneElement(child, {
        'aria-describedby': cx(child.props['aria-describedby'], id),
        onFocus: (e) => { show(); if (child.props.onFocus) child.props.onFocus(e); },
        onBlur: (e) => { hide(); if (child.props.onBlur) child.props.onBlur(e); },
      })}
      <span role="tooltip" id={id} className={cx('ui-tooltip', `ui-tooltip--${placement}`, open && 'is-open')}>
        {content}
      </span>
    </span>
  );
}
